import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import {
  ArrowLeftIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  MapPinIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import GoogleMap from '../../components/GoogleMap';

// Extend Window interface for Google Maps
declare global {
  interface Window {
    google: any;
  }
}

interface Destination {
  _id: string;
  name: string;
}

interface Amenity { name: string; icon: string }
interface UITestimonial { name: string; text: string; rating: number }

interface FormData {
  name: string;
  description: string;
  destination: string;
  location: string;
  coordinates: { lat: number; lng: number } | null;
  resortCode: string;
  isPartnerHotel: boolean;
  isActive: boolean;
  amenities: Amenity[];
  testimonials: UITestimonial[];
  bannerImage: File | null;
  gallery: File[];
}

const CreateResort: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    destination: '',
    location: '',
    coordinates: null,
    resortCode: '',
    isPartnerHotel: false,
    isActive: true, // Default to active for new resorts
    amenities: [],
    testimonials: [],
    bannerImage: null,
    gallery: []
  });

  const [newAmenity, setNewAmenity] = useState<{ name: string; icon: string }>({ name: '', icon: '' });
  const [newTestimonial, setNewTestimonial] = useState({ name: '', text: '', rating: 5 });
  const [previewImages, setPreviewImages] = useState<{ banner?: string; gallery: string[] }>({ gallery: [] });
  const [autocomplete, setAutocomplete] = useState<any>(null);
  const locationInputRef = useRef<HTMLInputElement>(null);

  const isEditing = Boolean(id);

  // Debug logging
  useEffect(() => {
    console.log('CreateResort useEffect triggered');
    console.log('isEditing:', isEditing);
    console.log('id:', id);

    // Load Google Maps API script if not already loaded
    const loadGoogleMapsAPI = () => {
      if (window.google && window.google.maps) {
        initializeAutocomplete();
        return;
      }

      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY || 'AIzaSyA6njPFHRLiKVAn7_6bWt1d_8sseEcv_Gg'}&libraries=places`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        initializeAutocomplete();
      };

      document.head.appendChild(script);
    };

    fetchDestinations();
    if (isEditing) {
      console.log('Calling fetchResortData for editing');
      fetchResortData();
    }
    loadGoogleMapsAPI();

    // Cleanup function
    return () => {
      if (autocomplete && window.google && window.google.maps) {
        window.google.maps.event.clearInstanceListeners(autocomplete);
      }
    };
  }, [id]);

  const initializeAutocomplete = () => {
    if (!locationInputRef.current || !window.google || !window.google.maps) return;

    const autocompleteInstance = new window.google.maps.places.Autocomplete(
      locationInputRef.current,
      {
        types: ['establishment', 'geocode'],
        fields: ['place_id', 'formatted_address', 'geometry', 'name']
      }
    );

    autocompleteInstance.addListener('place_changed', () => {
      const place = autocompleteInstance.getPlace();
      if (place.geometry && place.formatted_address) {
        setFormData(prev => ({
          ...prev,
          location: place.formatted_address,
          coordinates: {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng()
          }
        }));
      }
    });

    setAutocomplete(autocompleteInstance);
  };

  const fetchDestinations = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/destinations`);
      setDestinations(response.data.destinations || []);
    } catch (error: any) {
      console.error('Error fetching destinations:', error);
    }
  };

  const fetchResortData = async () => {
    if (!id) return;

    setDataLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts/${id}`);
      const resort = response.data.resort || response.data;

      console.log('Fetched resort data:', resort);

      if (!resort) {
        console.error('No resort data received');
        return;
      }

      setFormData({
        name: resort.name || '',
        description: resort.about || '',
        destination: (resort.destination && typeof resort.destination === 'object') ? resort.destination._id : resort.destination || '',
        location: (resort.location && (resort.location.address || '')) || '',
        coordinates: (resort.location && resort.location.coordinates && Array.isArray(resort.location.coordinates)) ? {
          lat: resort.location.coordinates[1], // MongoDB stores as [lng, lat]
          lng: resort.location.coordinates[0]
        } : null,
        resortCode: resort.resortCode || '',
        isPartnerHotel: resort.isPartnerHotel || false,
        isActive: resort.isActive !== undefined ? resort.isActive : true, // Default to active if not specified
        amenities: (resort.amenities || []).map((a: any) => ({ name: a.name || String(a), icon: a.icon || '' })),
        testimonials: (resort.testimonials || []).map((t: any) => ({ name: t.customerName, text: t.comment, rating: t.rating })),
        bannerImage: null,
        gallery: []
      });

      // Set existing image previews if they exist
      if (resort.bannerImage) {
        setPreviewImages(prev => ({ ...prev, banner: resort.bannerImage }));
      }

      if (resort.gallery && resort.gallery.length > 0) {
        setPreviewImages(prev => ({ ...prev, gallery: resort.gallery }));
      }

      console.log('Form data set successfully');
    } catch (error: any) {
      console.error('Error fetching resort:', error);
      console.error('Error response:', error?.response?.data);
      console.error('Error status:', error?.response?.status);
      
      // Handle specific error cases
      if (error?.response?.status === 404) {
        alert('Resort not found. It may have been deleted.');
        navigate('/admin/resorts');
      } else if (error?.response?.status === 401) {
        console.log('Authentication failed - redirecting to login');
        // Let axios interceptor handle this
      } else if (error?.response?.status === 400) {
        alert(`Invalid request: ${error?.response?.data?.message || 'Bad request'}`);
        navigate('/admin/resorts');
      } else {
        alert(`Failed to load resort data: ${error?.response?.data?.message || error.message}`);
      }
    } finally {
      setDataLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Auto-generate resort code from name
    if (name === 'name') {
      const code = value.toUpperCase().replace(/[^A-Z0-9]/g, '').substring(0, 8);
      setFormData(prev => ({ ...prev, resortCode: code }));
    }
  };

  const handleLocationSelect = (lat: number, lng: number, address: string) => {
    setFormData(prev => ({
      ...prev,
      coordinates: { lat, lng },
      location: address
    }));
  };

  const handleGetCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          setFormData(prev => ({
            ...prev,
            coordinates: { lat: latitude, lng: longitude }
          }));
        },
        (error: any) => {
          console.error('Error getting location:', error);
          alert('Unable to get your current location. Please enter the address manually.');
        }
      );
    } else {
      alert('Geolocation is not supported by this browser.');
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>, type: 'banner' | 'gallery') => {
    const files = e.target.files;
    if (!files) return;

    if (type === 'banner') {
      const file = files[0];
      setFormData(prev => ({ ...prev, bannerImage: file }));
      
      // Create preview
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewImages(prev => ({ ...prev, banner: reader.result as string }));
      };
      reader.readAsDataURL(file);
    } else {
      const fileArray = Array.from(files);
      setFormData(prev => ({ ...prev, gallery: [...prev.gallery, ...fileArray] }));
      
      // Create previews
      fileArray.forEach(file => {
        const reader = new FileReader();
        reader.onload = () => {
          setPreviewImages(prev => ({ 
            ...prev, 
            gallery: [...prev.gallery, reader.result as string] 
          }));
        };
        reader.readAsDataURL(file);
      });
    }
  };

  const addAmenity = () => {
    const name = newAmenity.name.trim();
    const icon = newAmenity.icon.trim() || 'check';
    if (name) {
      setFormData(prev => ({
        ...prev,
        amenities: [...prev.amenities, { name, icon }]
      }));
      setNewAmenity({ name: '', icon: '' });
    }
  };

  const removeAmenity = (index: number) => {
    setFormData(prev => ({
      ...prev,
      amenities: prev.amenities.filter((_, i) => i !== index)
    }));
  };

  const addTestimonial = () => {
    if (newTestimonial.name.trim() && newTestimonial.text.trim()) {
      setFormData(prev => ({
        ...prev,
        testimonials: [...prev.testimonials, { ...newTestimonial }]
      }));
      setNewTestimonial({ name: '', text: '', rating: 5 });
    }
  };

  const removeTestimonial = (index: number) => {
    setFormData(prev => ({
      ...prev,
      testimonials: prev.testimonials.filter((_, i) => i !== index)
    }));
  };

  const removeGalleryImage = (index: number) => {
    setFormData(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
    setPreviewImages(prev => ({
      ...prev,
      gallery: prev.gallery.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Client-side validation
    if (!formData.name.trim()) {
      alert('Please enter a resort name');
      return;
    }
    if (!formData.resortCode.trim()) {
      alert('Please enter a resort code');
      return;
    }
    if (!formData.destination) {
      alert('Please select a destination');
      return;
    }
    if (!formData.description.trim() || formData.description.trim().length < 10) {
      alert('Please enter a resort description (minimum 10 characters)');
      return;
    }
    if (!formData.bannerImage && !isEditing) {
      alert('Please upload a banner image');
      return;
    }
    
    setLoading(true);

    try {
      const submitData = new FormData();
      
      // Add text fields
      submitData.append('name', formData.name);
      // Backend expects 'about'
      submitData.append('about', formData.description);
      submitData.append('destination', formData.destination);

      // Include coordinates if available
      const locationData: any = { address: formData.location };
      if (formData.coordinates) {
        locationData.coordinates = [formData.coordinates.lng, formData.coordinates.lat]; // MongoDB expects [lng, lat]
      }
      submitData.append('location', JSON.stringify(locationData));

      submitData.append('resortCode', formData.resortCode);
      submitData.append('isPartnerHotel', formData.isPartnerHotel.toString());
      submitData.append('isActive', formData.isActive.toString());
      const normalizedAmenities = formData.amenities.map(a => ({ name: a.name, icon: (a.icon || '').trim() || 'check' }));
      submitData.append('amenities', JSON.stringify(normalizedAmenities));
      // Map testimonials to backend shape
      const mappedTestimonials = formData.testimonials.map(t => ({ customerName: t.name, comment: t.text, rating: t.rating }));
      submitData.append('testimonials', JSON.stringify(mappedTestimonials));
      
      // Add banner image
      if (formData.bannerImage) {
        submitData.append('bannerImage', formData.bannerImage);
      }
      
      // Add gallery images
      formData.gallery.forEach((file) => {
        submitData.append('gallery', file);
      });

      const url = isEditing 
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts/${id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/resorts`;
      
      const method = isEditing ? 'PUT' : 'POST';

      const response = await axios({
        method,
        url,
        data: submitData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log('Resort saved successfully:', response.data);
      alert('Resort saved successfully!');
      navigate('/admin/resorts');
    } catch (error: any) {
      console.error('Error saving resort:', error);
      console.error('Error response:', error?.response?.data);
      
      // Show specific validation errors
      if (error?.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const errorMessages = error.response.data.errors
          .map((err: any) => `${err.path || err.param}: ${err.msg}`)
          .join('\n');
        alert(`Validation errors:\n\n${errorMessages}`);
      } else if (error?.response?.data?.message) {
        alert(`Error: ${error.response.data.message}`);
      } else {
        alert('An error occurred while saving the resort. Please check all required fields.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>{isEditing ? 'Edit Resort' : 'Create Resort'} | Bali Getaway Admin</title>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Header */}
          <div className="mb-8">
            <button
              onClick={() => navigate('/admin/resorts')}
              className="flex items-center text-gray-600 hover:text-gray-800 mb-4 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5 mr-2" />
              Back to Resorts
            </button>
            
            <div className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-700 to-orange-700 bg-clip-text text-transparent">
                🏨 {isEditing ? 'Edit Resort' : 'Create New Resort'}
              </h1>
              <p className="text-gray-600 mt-2">
                {isEditing ? 'Update resort information and settings' : 'Add a new luxury resort to your collection'}
              </p>
              {/* Stepper */}
              <div className="mt-6 hidden md:block">
                <div className="flex items-center justify-between">
                  {[
                    { id: 'basic', label: 'Basic' },
                    { id: 'images', label: 'Images' },
                    { id: 'amenities', label: 'Amenities' },
                    { id: 'testimonials', label: 'Testimonials' }
                  ].map((step, idx) => (
                    <button
                      key={step.id}
                      type="button"
                      onClick={() => document.getElementById(step.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}
                      className="group flex-1 flex items-center"
                      title={step.label}
                    >
                      <div className="flex items-center">
                        <span className="flex items-center justify-center h-8 w-8 rounded-full bg-gradient-to-r from-yellow-500 to-orange-600 text-white text-sm font-semibold shadow">
                          {idx + 1}
                        </span>
                        <span className="ml-3 text-sm font-medium text-gray-700 group-hover:text-gray-900">{step.label}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Loading Overlay for Data Fetching */}
          {dataLoading && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-2xl p-8 flex flex-col items-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600 mb-4"></div>
                <p className="text-gray-900 font-medium">Loading resort data...</p>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <div id="basic" className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                📝 Basic Information
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resort Name *
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    placeholder="Enter resort name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resort Code *
                  </label>
                  <input
                    type="text"
                    name="resortCode"
                    value={formData.resortCode}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                    placeholder="Enter unique resort code"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Destination *
                  </label>
                  <select
                    name="destination"
                    value={formData.destination}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                  >
                    <option value="">Select destination</option>
                    {destinations.map(dest => (
                      <option key={dest._id} value={dest._id}>{dest.name}</option>
                    ))}
                  </select>
                </div>

                {/* Description/About Field */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    About Resort * (Minimum 10 characters)
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    minLength={10}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all resize-none"
                    placeholder="Describe the resort, its features, ambiance, and what makes it special..."
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Current length: {formData.description.length} characters
                  </p>
                </div>

                {/* Location Section */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Resort Location
                  </label>

                  {/* Address Input with Location Button */}
                  <div className="flex gap-3 mb-4">
                    <div className="relative flex-1">
                      <MapPinIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3.5" />
                      <input
                        ref={locationInputRef}
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleInputChange}
                        className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent transition-all"
                        placeholder="Enter resort name or address (autocomplete enabled)"
                        autoComplete="off"
                      />
                      <button
                        type="button"
                        onClick={handleGetCurrentLocation}
                        className="absolute right-3 top-3.5 p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Use current location"
                      >
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Map Display */}
                  {formData.coordinates && (
                    <div className="mb-4">
                      <GoogleMap
                        latitude={formData.coordinates.lat}
                        longitude={formData.coordinates.lng}
                        name={formData.name || 'Resort Location'}
                        address={formData.location}
                        height="h-64"
                        className="rounded-xl"
                      />
                    </div>
                  )}

                  {/* Coordinates Display */}
                  {formData.coordinates && (
                    <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <MapPinIcon className="h-5 w-5 text-blue-600" />
                      <span className="text-sm text-blue-800">
                        📍 {formData.coordinates.lat.toFixed(6)}, {formData.coordinates.lng.toFixed(6)}
                      </span>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, coordinates: null }))}
                        className="ml-auto text-blue-600 hover:text-blue-800 text-sm"
                      >
                        Clear location
                      </button>
                    </div>
                  )}

                  {/* Location Helper Text */}
                  <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="flex items-start space-x-2">
                      <div className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center mt-0.5">
                        <span className="text-blue-600 text-xs">ℹ️</span>
                      </div>
                      <div className="text-sm text-blue-800">
                        <p className="font-medium mb-1">How to set location:</p>
                        <ul className="space-y-1 text-blue-700">
                          <li>• Start typing a hotel name or address - suggestions will appear</li>
                          <li>• Select from the dropdown for accurate coordinates</li>
                          <li>• Or use "Get current location" for automatic detection</li>
                          <li>• The map will update automatically when location is set</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Partner Hotel and Active Status */}
              <div className="mb-6 space-y-4">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isPartnerHotel"
                    checked={formData.isPartnerHotel}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-yellow-600 border-gray-300 rounded focus:ring-yellow-500"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    Partner Hotel
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Check if this is a partner hotel
                </p>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                  />
                  <label className="ml-3 text-sm font-medium text-gray-700">
                    ✅ Active Resort
                  </label>
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Active resorts are visible in package creation and bookings
                </p>
              </div>
            </div>

            {/* Amenities */}
            <div id="amenities" className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                🏨 Amenities
              </h2>
              <p className="text-sm text-gray-500 mb-6">Add amenities with an icon for a polished presentation on the resort page.</p>
              
              {/* Amenities content will be added here */}
              <div className="space-y-4">
                <p className="text-gray-600">Amenity management functionality to be implemented.</p>
              </div>
            </div>

            {/* Images */}
            <div id="images" className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                📸 Images
              </h2>
              
              {/* Banner Image */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Banner Image *
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-yellow-500 transition-colors">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleImageChange(e, 'banner')}
                    className="hidden"
                    id="banner-upload"
                  />
                  <label htmlFor="banner-upload" className="cursor-pointer">
                    {previewImages.banner ? (
                      <img src={previewImages.banner} alt="Banner preview" className="w-full h-48 object-cover rounded-lg" />
                    ) : (
                      <div className="py-8">
                        <PhotoIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">Click to upload banner image</p>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              {/* Gallery Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Gallery Images
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center hover:border-yellow-500 transition-colors mb-4">
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => handleImageChange(e, 'gallery')}
                    className="hidden"
                    id="gallery-upload"
                  />
                  <label htmlFor="gallery-upload" className="cursor-pointer">
                    <PhotoIcon className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">Click to add gallery images</p>
                  </label>
                </div>
                
                {previewImages.gallery.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {previewImages.gallery.map((image, index) => (
                      <div key={index} className="relative">
                        <img src={image} alt={`Gallery ${index + 1}`} className="w-full h-24 object-cover rounded-lg" />
                        <button
                          type="button"
                          onClick={() => removeGalleryImage(index)}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Amenities */}
            <div id="amenities" className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-2 flex items-center">
                🏊‍♀️ Amenities
              </h2>
              <p className="text-sm text-gray-500 mb-6">Add amenities with an icon for a polished presentation on the resort page.</p>

              {/* New Amenity Row */}
              <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mb-4">
                <div className="md:col-span-7">
                  <input
                    type="text"
                    value={newAmenity.name}
                    onChange={(e) => setNewAmenity(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="Amenity name (e.g., Infinity Pool)"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-3">
                  <div className="relative">
                    <select
                      value={newAmenity.icon}
                      onChange={(e) => setNewAmenity(prev => ({ ...prev, icon: e.target.value }))}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-yellow-500 focus:border-transparent"
                    >
                      <option value="">Choose icon</option>
                      <option value="pool">🏊 Pool</option>
                      <option value="wifi">📶 Wi‑Fi</option>
                      <option value="spa">💆 Spa</option>
                      <option value="gym">🏋️ Gym</option>
                      <option value="beach">🏖️ Beach</option>
                      <option value="dining">🍽️ Dining</option>
                      <option value="bar">🍹 Bar</option>
                      <option value="parking">🅿️ Parking</option>
                      <option value="ac">❄️ Air Conditioning</option>
                      <option value="tv">📺 TV</option>
                      <option value="laundry">🧺 Laundry</option>
                      <option value="room_service">🛎️ Room Service</option>
                      <option value="breakfast">🥐 Breakfast</option>
                      <option value="shuttle">🚌 Airport Shuttle</option>
                      <option value="non_smoking">🚭 Non‑Smoking</option>
                      <option value="kids">🧒 Kids Friendly</option>
                      <option value="pet_friendly">🐶 Pet Friendly</option>
                    </select>
                  </div>
                </div>
                <div className="md:col-span-2 flex">
                  <button
                    type="button"
                    onClick={addAmenity}
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-600 text-white rounded-xl hover:from-yellow-600 hover:to-orange-700 shadow transition"
                  >
                    <PlusIcon className="h-5 w-5" /> Add
                  </button>
                </div>
              </div>

              {/* Amenity List */}
              {formData.amenities.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {formData.amenities.map((a, index) => (
                    <div key={index} className="flex items-center justify-between bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center text-xl">
                          {(() => {
                            const map: Record<string, string> = {
                              pool: '🏊', wifi: '📶', spa: '💆', gym: '🏋️', beach: '🏖️', dining: '🍽️', bar: '🍹', parking: '🅿️', ac: '❄️', tv: '📺', laundry: '🧺', room_service: '🛎️', breakfast: '🥐', shuttle: '🚌', non_smoking: '🚭', kids: '🧒', pet_friendly: '🐶'
                            };
                            return <span>{map[(a.icon || '').toLowerCase()] || '✓'}</span>;
                          })()}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{a.name}</div>
                          {a.icon && <div className="text-xs text-gray-500">{a.icon.replace('_', ' ')}</div>}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAmenity(index)}
                        className="text-red-500 hover:text-red-700"
                        title="Remove"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-gray-500 text-sm">No amenities added yet.</div>
              )}
            </div>

            {/* Testimonials */}
            <div id="testimonials" className="bg-white/80 backdrop-blur rounded-2xl border border-orange-200/50 p-6 shadow-xl">
              <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center">
                💭 Testimonials
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 p-4 bg-gray-50 rounded-xl">
                <input
                  type="text"
                  value={newTestimonial.name}
                  onChange={(e) => setNewTestimonial(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Customer name"
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                />
                <div className="flex items-center gap-2">
                  <StarIcon className="h-5 w-5 text-yellow-500" />
                  <select
                    value={newTestimonial.rating}
                    onChange={(e) => setNewTestimonial(prev => ({ ...prev, rating: parseInt(e.target.value) }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  >
                    {[1, 2, 3, 4, 5].map(rating => (
                      <option key={rating} value={rating}>{rating} Stars</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={addTestimonial}
                  className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                >
                  Add
                </button>
                <textarea
                  value={newTestimonial.text}
                  onChange={(e) => setNewTestimonial(prev => ({ ...prev, text: e.target.value }))}
                  placeholder="Testimonial text"
                  className="md:col-span-3 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500"
                  rows={2}
                />
              </div>
              
              <div className="space-y-3">
                {formData.testimonials.map((testimonial, index) => (
                  <div key={index} className="bg-white p-4 rounded-xl border border-gray-200">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium">{testimonial.name}</span>
                          <div className="flex text-yellow-400">
                            {Array.from({ length: testimonial.rating }).map((_, i) => (
                              <StarIcon key={i} className="h-4 w-4 fill-current" />
                            ))}
                          </div>
                        </div>
                        <p className="text-gray-600">{testimonial.text}</p>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeTestimonial(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={loading || dataLoading || (!isEditing && !formData.name.trim())}
                    className="px-8 py-3 bg-gradient-to-r from-yellow-500 to-orange-600 text-white font-semibold rounded-xl hover:from-yellow-600 hover:to-orange-700 shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        {isEditing ? 'Updating...' : 'Creating...'}
                      </div>
                    ) : dataLoading ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        'Loading...'
                      </div>
                    ) : (
                      isEditing ? '✅ Update Resort' : '🚀 Create Resort'
                    )}
                  </button>
                </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default CreateResort;
